import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { surveyTemplateApi } from '../api/surveyTemplateApi';
import { surveyTemplateQueryKeys } from './queryKeys';
import { CreateSurveyTemplateDTO, UpdateSurveyTemplateDTO } from '../dtos';
import { useNotifications } from '@shared/components/notifications';

// Get survey templates by tenant
export const useSurveyTemplatesByTenant = (tenantId: string) => {
  return useQuery({
    queryKey: surveyTemplateQueryKeys.templatesByTenant(tenantId),
    queryFn: () => surveyTemplateApi.getTemplatesByTenant(tenantId),
    enabled: !!tenantId,
  });
};

// Get survey template by ID
export const useSurveyTemplate = (tenantId: string, templateId: string) => {
  return useQuery({
    queryKey: surveyTemplateQueryKeys.template(tenantId, templateId),
    queryFn: () => surveyTemplateApi.getTemplate(tenantId, templateId),
    enabled: !!tenantId && !!templateId,
  });
};

// Create survey template mutation
export const useCreateSurveyTemplate = (tenantId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (data: CreateSurveyTemplateDTO) => surveyTemplateApi.createTemplate(tenantId, data),
    onSuccess: (_response) => {
      // Invalidate and refetch templates for this tenant
      queryClient.invalidateQueries({
        queryKey: surveyTemplateQueryKeys.templatesByTenant(tenantId),
      });

      notifications.success('Survey template created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to create survey template';
      notifications.error(message);
    },
  });
};

// Update survey template mutation
export const useUpdateSurveyTemplate = (tenantId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: UpdateSurveyTemplateDTO }) =>
      surveyTemplateApi.updateTemplate(tenantId, templateId, data),
    onSuccess: (response, variables) => {
      // Invalidate specific template
      queryClient.invalidateQueries({
        queryKey: surveyTemplateQueryKeys.template(tenantId, variables.templateId),
      });

      // Invalidate templates list for this tenant
      queryClient.invalidateQueries({
        queryKey: surveyTemplateQueryKeys.templatesByTenant(tenantId),
      });

      notifications.success('Survey template updated successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to update survey template';
      notifications.error(message);
    },
  });
};

// Delete survey template mutation
export const useDeleteSurveyTemplate = (tenantId: string) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: (templateId: string) => surveyTemplateApi.deleteTemplate(tenantId, templateId),
    onSuccess: (_, templateId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: surveyTemplateQueryKeys.template(tenantId, templateId),
      });

      // Invalidate templates list for this tenant
      queryClient.invalidateQueries({
        queryKey: surveyTemplateQueryKeys.templatesByTenant(tenantId),
      });

      notifications.success('Survey template deleted successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'Failed to delete survey template';
      notifications.error(message);
    },
  });
};

// For data management - simplified interface
export const useSurveyTemplatesForDataManagement = (tenantId: string) => {
  const query = useSurveyTemplatesByTenant(tenantId);

  return {
    ...query,
    templates: query.data?.data?.templates || [],
    total: query.data?.data?.total || 0,
  };
};
